import React from 'react';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Timeline } from 'primereact/timeline';
import { Card } from 'primereact/card';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { WorkflowExecution } from '../types';
import WorkflowErrorHandler from './WorkflowErrorHandler';
import WorkflowStepDetails from './WorkflowStepDetails';

interface WorkflowProgressProps {
  workflow: WorkflowExecution | null;
  onRefresh?: () => void;
  loading?: boolean;
  showHistory?: boolean;
  onPause?: () => Promise<boolean>;
  onResume?: () => Promise<boolean>;
  onCancel?: () => Promise<boolean>;
  onRetry?: () => Promise<string | null>;
  onContactSupport?: (errorDetails: string) => void;
}

interface WorkflowStep {
  name: string;
  label: string;
  description: string;
  icon: string;
  estimatedDuration: number; // in seconds
  color: string;
}

const WORKFLOW_STEPS: WorkflowStep[] = [
  {
    name: 'ExtractIndicators',
    label: 'Extract Indicators',
    description: 'Analyzing TOR document and extracting measurable indicators',
    icon: 'pi pi-search',
    estimatedDuration: 120, // 2 minutes
    color: '#3B82F6'
  },
  {
    name: 'DesignQuestionnaire',
    label: 'Design Questionnaire',
    description: 'Converting indicators into structured survey questions',
    icon: 'pi pi-list',
    estimatedDuration: 180, // 3 minutes
    color: '#22C55E'
  },
  {
    name: 'DeployToKobo',
    label: 'Deploy to KoboToolbox',
    description: 'Converting to XLSForm and deploying to KoboToolbox',
    icon: 'pi pi-send',
    estimatedDuration: 90, // 1.5 minutes
    color: '#F97316'
  }
];

const WorkflowProgress: React.FC<WorkflowProgressProps> = ({
  workflow,
  onRefresh,
  loading = false,
  showHistory = false,
  onPause,
  onCancel,
  onRetry,
  onContactSupport
}) => {
  const getStatusSeverity = (status: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return 'success';
      case 'FAILED':
      case 'TIMED_OUT':
      case 'ABORTED':
        return 'danger';
      case 'RUNNING':
        return 'info';
      default:
        return 'warning';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return 'Running';
      case 'SUCCEEDED':
        return 'Completed';
      case 'FAILED':
        return 'Failed';
      case 'TIMED_OUT':
        return 'Timed Out';
      case 'ABORTED':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const getCurrentStepIndex = (step?: string): number => {
    if (!step) return 0;
    return WORKFLOW_STEPS.findIndex(s => s.name === step);
  };

  const getCurrentStepInfo = (step?: string): WorkflowStep | null => {
    if (!step) return null;
    return WORKFLOW_STEPS.find(s => s.name === step) || null;
  };

  const getProgressValue = (status: string, currentStep?: string): number => {
    if (status === 'SUCCEEDED') return 100;
    if (status === 'FAILED' || status === 'TIMED_OUT' || status === 'ABORTED') {
      // Show progress up to the failed step
      const stepIndex = getCurrentStepIndex(currentStep);
      return Math.max(0, (stepIndex / WORKFLOW_STEPS.length) * 100);
    }
    
    // Calculate progress based on current step
    const stepIndex = getCurrentStepIndex(currentStep);
    const baseProgress = (stepIndex / WORKFLOW_STEPS.length) * 100;
    const stepProgress = 100 / WORKFLOW_STEPS.length;
    
    // Add partial progress within the current step (assume 50% completion)
    return Math.min(100, baseProgress + (stepProgress * 0.5));
  };

  const getEstimatedTimeRemaining = (status: string, currentStep?: string, startedAt?: string): string => {
    if (status !== 'RUNNING' || !currentStep || !startedAt) return '';
    
    const currentStepIndex = getCurrentStepIndex(currentStep);
    const remainingSteps = WORKFLOW_STEPS.slice(currentStepIndex);
    const totalRemainingTime = remainingSteps.reduce((sum, step) => sum + step.estimatedDuration, 0);
    
    // Calculate elapsed time for current step
    const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
    const currentStepDuration = WORKFLOW_STEPS[currentStepIndex]?.estimatedDuration || 0;
    const currentStepRemaining = Math.max(0, currentStepDuration - elapsed);
    
    const totalRemaining = totalRemainingTime - currentStepDuration + currentStepRemaining;
    
    if (totalRemaining < 60) return `~${totalRemaining}s remaining`;
    if (totalRemaining < 3600) return `~${Math.ceil(totalRemaining / 60)}m remaining`;
    return `~${Math.ceil(totalRemaining / 3600)}h remaining`;
  };

  const getElapsedTime = (startedAt: string, endedAt?: string): string => {
    if (!startedAt) return 'N/A';
    
    const start = new Date(startedAt).getTime();
    if (isNaN(start)) return 'N/A';
    
    const end = endedAt ? new Date(endedAt).getTime() : Date.now();
    if (isNaN(end)) return 'N/A';
    
    const elapsed = Math.floor((end - start) / 1000);
    
    // Handle negative or invalid durations
    if (elapsed < 0) return 'N/A';
    if (elapsed < 1) return '< 1s';
    if (elapsed < 60) return `${elapsed}s`;
    if (elapsed < 3600) {
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      return `${minutes}m ${seconds}s`;
    }
    
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const renderTimelineEvents = () => {
    if (!workflow) return [];
    
    const currentStepIndex = getCurrentStepIndex(workflow.current_step);
    
    return WORKFLOW_STEPS.map((step, index) => {
      let status: 'completed' | 'current' | 'pending' | 'failed' = 'pending';
      
      if (workflow.status === 'SUCCEEDED' || index < currentStepIndex) {
        status = 'completed';
      } else if (index === currentStepIndex) {
        status = workflow.status === 'FAILED' ? 'failed' : 'current';
      }
      
      return {
        status,
        date: status === 'completed' ? 'Completed' : 
              status === 'current' ? 'In Progress' :
              status === 'failed' ? 'Failed' : 'Pending',
        icon: step.icon,
        color: status === 'completed' ? '#22C55E' :
               status === 'current' ? step.color :
               status === 'failed' ? '#EF4444' : '#9CA3AF',
        title: step.label,
        description: step.description,
        estimatedDuration: `~${Math.ceil(step.estimatedDuration / 60)}m`
      };
    });
  };

  if (!workflow) {
    return null;
  }

  // Show error handler for failed workflows
  if (['FAILED', 'TIMED_OUT', 'ABORTED'].includes(workflow.status)) {
    return (
      <WorkflowErrorHandler
        workflow={workflow}
        onRetry={onRetry}
        onCancel={onCancel}
        onContactSupport={onContactSupport}
      />
    );
  }

  const progressValue = getProgressValue(workflow.status, workflow.current_step);
  const isRunning = workflow.status === 'RUNNING';
  const currentStepInfo = getCurrentStepInfo(workflow.current_step);
  const timeRemaining = getEstimatedTimeRemaining(workflow.status, workflow.current_step, workflow.started_at);
  const elapsedTime = getElapsedTime(workflow.started_at, workflow.ended_at);

  return (
    <Card className="workflow-progress-card">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h3 style={{
            fontSize: '1.25rem',
            margin: '0 0 0.25rem 0',
            color: '#1f2937'
          }}>
            Workflow Progress
          </h3>
          {currentStepInfo && isRunning && (
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              margin: 0
            }}>
              {currentStepInfo.description}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Tag
            value={getStatusLabel(workflow.status)}
            severity={getStatusSeverity(workflow.status)}
            style={{ fontSize: '0.75rem' }}
          />
          {onRefresh && (
            <Button
              icon="pi pi-refresh"
              text
              size="small"
              onClick={onRefresh}
              loading={loading}
              tooltip="Refresh status"
            />
          )}
        </div>
      </div>

      {/* Progress Bar Section */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem'
        }}>
          <span style={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#374151'
          }}>
            {Math.round(progressValue)}% Complete
          </span>
          <div style={{
            fontSize: '0.75rem',
            color: '#6b7280'
          }}>
            {isRunning && timeRemaining && (
              <span>{timeRemaining} • </span>
            )}
            <span>Elapsed: {elapsedTime}</span>
          </div>
        </div>
        <ProgressBar
          value={progressValue}
          style={{ height: '8px' }}
          color={workflow.status === 'FAILED' ? '#EF4444' : '#3B82F6'}
        />
      </div>

      {/* Current Step Info */}
      {isRunning && currentStepInfo && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <div style={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '50%',
              backgroundColor: currentStepInfo.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className={currentStepInfo.icon} style={{ color: 'white', fontSize: '1rem' }}></i>
            </div>
            <div>
              <div style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#1f2937',
                marginBottom: '0.25rem'
              }}>
                {currentStepInfo.label}
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: '#6b7280'
              }}>
                {currentStepInfo.description}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workflow Controls */}
      {isRunning && (onPause || onCancel) && (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {onPause && (
            <Button
              label="Pause"
              icon="pi pi-pause"
              size="small"
              text
              onClick={onPause}
            />
          )}
          {onCancel && (
            <Button
              label="Cancel"
              icon="pi pi-times"
              size="small"
              text
              severity="danger"
              onClick={onCancel}
            />
          )}
        </div>
      )}

      {/* Success Message */}
      {workflow.status === 'SUCCEEDED' && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f0fdf4',
          border: '1px solid #22c55e',
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <i className="pi pi-check-circle" style={{ color: '#22c55e', fontSize: '1.25rem' }}></i>
            <div>
              <div style={{
                fontSize: '0.875rem',
                color: '#15803d',
                fontWeight: 500,
                marginBottom: '0.25rem'
              }}>
                Workflow completed successfully
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: '#166534'
              }}>
                All steps completed in {elapsedTime}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline View */}
      {showHistory && (
        <div>
          <h4 style={{
            fontSize: '1rem',
            margin: '0 0 1rem 0',
            color: '#1f2937'
          }}>
            Workflow Steps
          </h4>
          <Timeline
            value={renderTimelineEvents()}
            align="left"
            className="workflow-timeline"
            marker={(item) => (
              <div style={{
                width: '2rem',
                height: '2rem',
                borderRadius: '50%',
                backgroundColor: item.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <i className={item.icon} style={{ color: 'white', fontSize: '0.75rem' }}></i>
              </div>
            )}
            content={(item) => (
              <div style={{ marginLeft: '1rem' }}>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#1f2937',
                  marginBottom: '0.25rem'
                }}>
                  {item.title}
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}>
                  {item.description}
                </div>
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center'
                }}>
                  <Tag
                    value={item.date}
                    severity={
                      item.status === 'completed' ? 'success' :
                      item.status === 'current' ? 'info' :
                      item.status === 'failed' ? 'danger' : 'secondary'
                    }
                    style={{ fontSize: '0.625rem' }}
                  />
                  <span style={{
                    fontSize: '0.625rem',
                    color: '#9ca3af'
                  }}>
                    {item.estimatedDuration}
                  </span>
                </div>
              </div>
            )}
          />

          {/* Detailed Step Information */}
          <div style={{ marginTop: '1.5rem' }}>
            <h4 style={{
              fontSize: '1rem',
              margin: '0 0 1rem 0',
              color: '#1f2937'
            }}>
              Step Details
            </h4>
            <Accordion multiple>
              {WORKFLOW_STEPS.map((step, index) => {
                const currentStepIndex = getCurrentStepIndex(workflow.current_step);
                let stepStatus: 'completed' | 'running' | 'pending' | 'failed' = 'pending';
                
                if (workflow.status === 'SUCCEEDED' || index < currentStepIndex) {
                  stepStatus = 'completed';
                } else if (index === currentStepIndex) {
                  stepStatus = workflow.status === 'FAILED' ? 'failed' : 'running';
                }

                return (
                  <AccordionTab
                    key={index}
                    header={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className={step.icon} style={{ fontSize: '0.875rem' }}></i>
                        <span>{step.label}</span>
                      </div>
                    }
                  >
                    <WorkflowStepDetails
                      stepName={step.label}
                      stepStatus={stepStatus}
                      stepInput={{
                        // Mock data - replace with actual step input from workflow
                        step: step.name,
                        timestamp: workflow.started_at
                      }}
                      stepOutput={
                        stepStatus === 'completed' ? {
                          status: 'success',
                          completedAt: new Date().toISOString()
                        } : undefined
                      }
                      stepLogs={[
                        `[${new Date().toLocaleTimeString()}] INFO: Starting ${step.label}`,
                        `[${new Date().toLocaleTimeString()}] INFO: Processing data...`,
                        stepStatus === 'completed' ? `[${new Date().toLocaleTimeString()}] INFO: ${step.label} completed successfully` :
                        stepStatus === 'running' ? `[${new Date().toLocaleTimeString()}] INFO: ${step.label} in progress...` :
                        stepStatus === 'failed' ? `[${new Date().toLocaleTimeString()}] ERROR: ${step.label} failed` : ''
                      ].filter(Boolean)}
                      stepMetrics={{
                        duration: stepStatus === 'completed' ? step.estimatedDuration * 1000 : undefined,
                        memory: stepStatus === 'completed' ? 128 * 1024 * 1024 : undefined,
                        apiCalls: stepStatus === 'completed' ? 5 : undefined
                      }}
                      stepRetryHistory={
                        stepStatus === 'failed' ? [
                          {
                            attempt: 1,
                            timestamp: new Date(Date.now() - 60000).toISOString(),
                            status: 'failed',
                            error: 'Connection timeout'
                          }
                        ] : []
                      }
                    />
                  </AccordionTab>
                );
              })}
            </Accordion>
          </div>
        </div>
      )}
    </Card>
  );
};

export default WorkflowProgress;